import { useList } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Tag, Input, Collapse, Button, Space, Spin, Select, Alert, Empty } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';

import { Permission } from './types';
import {
  GENERAL_GROUP,
  groupLabel,
  groupPermissions,
  permissionCategory,
  permissionGroup,
} from './grouping';

// The area filter's value: an area on its own, or an area and one group inside it.
const FILTER_SEP = '\u0000';

export const PermissionsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState<string | undefined>(undefined);

  const { data, isLoading, isError } = useList<Permission>({
    resource: 'permissions',
    meta: { dataProviderName: 'authDataProvider' },
    pagination: { mode: 'off' },
  });

  const permissions = useMemo(() => data?.data ?? [], [data]);

  // Options come from every permission, so a group stays pickable while a search hides it.
  const filterOptions = useMemo(
    () =>
      groupPermissions(permissions).map(({ category, count, groups }) => ({
        label: category,
        options: [
          { value: category, label: `All ${category} (${count})` },
          ...(groups.length > 1 || groups[0]?.group !== GENERAL_GROUP
            ? groups.map(({ group, items }) => ({
                value: `${category}${FILTER_SEP}${group}`,
                label: `${category} / ${groupLabel(group)} (${items.length})`,
              }))
            : []),
        ],
      })),
    [permissions],
  );

  const filtered = useMemo(() => {
    let list = permissions;
    if (areaFilter !== undefined) {
      const [category, group] = areaFilter.split(FILTER_SEP);
      list = list.filter(
        (p) =>
          permissionCategory(p) === category &&
          (group === undefined || permissionGroup(p.key) === group),
      );
    }
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (p) =>
        p.key.toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q),
    );
  }, [permissions, search, areaFilter]);

  const grouped = useMemo(() => groupPermissions(filtered), [filtered]);

  const columns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc: string) => desc || '—',
    },
    {
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      width: 120,
      render: (scope: string) => (
        <Tag color={scope === 'customer' ? 'orange' : 'purple'}>{scope || 'internal'}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isLive',
      key: 'isLive',
      width: 120,
      render: (isLive: boolean) =>
        isLive ? <Tag color="green">Live</Tag> : <Tag color="default">Placeholder</Tag>,
    },
  ];

  return (
    <List
      title="Permissions"
      headerButtons={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/permissions/create')}
        >
          Create Permission
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          <Input.Search
            placeholder="Search by key, description, or category..."
            allowClear
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 480, maxWidth: '100%' }}
          />
          <Select
            aria-label="Area or group"
            placeholder="All areas and groups"
            allowClear
            showSearch
            optionFilterProp="label"
            value={areaFilter}
            onChange={(v?: string) => setAreaFilter(v)}
            options={filterOptions}
            style={{ width: 280 }}
          />
        </Space>

        {isLoading ? (
          <Spin />
        ) : isError ? (
          <Alert type="error" showIcon message="Couldn't load the permissions list" />
        ) : grouped.length === 0 ? (
          <Empty
            description={
              permissions.length === 0
                ? 'No permissions yet'
                : 'No permissions match the search or filter'
            }
          />
        ) : (
          <Collapse
            // A new filter or search opens every area and group again, so a chosen group is
            // never shown folded away (open/closed state only applies while the filter stays).
            key={`${areaFilter ?? ''}${FILTER_SEP}${search}`}
            defaultActiveKey={grouped.map(({ category }) => category)}
            items={grouped.map(({ category, count, groups }) => {
              const table = (items: Permission[]) => (
                <Table
                  dataSource={items}
                  columns={columns}
                  rowKey={(record) => record.key}
                  pagination={false}
                  size="small"
                  onRow={(record) => ({
                    onClick: () => navigate(`/permissions/${encodeURIComponent(record.key)}`),
                    style: { cursor: 'pointer' },
                  })}
                />
              );
              // An area with only one group shows one table, as before.
              const flat = groups.length === 1;
              return {
                key: category,
                label: (
                  <span style={{ fontWeight: 600, fontSize: 15, textTransform: 'capitalize' }}>
                    {category} ({count})
                  </span>
                ),
                children: flat ? (
                  table(groups[0].items)
                ) : (
                  // Groups are lighter boxes with a left accent, so they read as part of the area.
                  <Collapse
                    ghost
                    size="small"
                    defaultActiveKey={groups.map(({ group }) => `g:${group}`)}
                    items={groups.map(({ group, items }) => ({
                      key: `g:${group}`,
                      label: (
                        <span style={{ fontWeight: 500, color: '#595959' }}>
                          {groupLabel(group)} ({items.length})
                        </span>
                      ),
                      style: {
                        borderLeft: '3px solid #d9d9d9',
                        background: '#fafafa',
                        borderRadius: 6,
                        marginBottom: 8,
                      },
                      children: table(items),
                    }))}
                  />
                ),
              };
            })}
          />
        )}
      </Space>
    </List>
  );
};
